import { Client, ClientsFilter } from '@entities/clients';
import { useCallback, useEffect, useState } from 'react';
import { gql, useQuery } from 'urql';

const GET_SMINEX_CLUB_CLIENTS_QUERY = gql`
  query GetSminexClubClients(
    $page: Int!
    $pageSize: Int!
    $searchString: String
    $orderBy: SminexClubClientsOrderBy
    $clientsFilter: SminexClubEventFilter
  ) {
    getSminexClubClients(
      page: $page
      pageSize: $pageSize
      searchString: $searchString
      orderBy: $orderBy
      clientsFilter: $clientsFilter
    ) {
      count
      total
      page
      pageCount
      sminexClubClients {
        id
        participantId
        firstName
        lastName
        surname
        phone
        age
        photoUrl
        maritalStatus
        countChildren
        brokerName
        notes
        doNotCall
        createdAt
        updatedAt
        managerId
        events {
          eventId
          eventName
          checked
        }
        projects {
          amount
          project {
            id
            name
            type
            active
          }
        }
        manager {
          id
          participantId
          firstName
          lastName
          surname
          active
        }
      }
    }
  }
`;

interface ClientsResponse {
  getSminexClubClients: {
    count: number;
    page: number;
    pageCount: number;
    total: number;
    sminexClubClients: Client[];
  };
}

export const CLIENTS_PAGE_SIZE = 12;

export const useFetchClients = (withoutPagination?: boolean) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [clientsFilter, setClientsFilter] = useState<ClientsFilter>({});

  const [{ data, fetching, error }, executeQuery] = useQuery<ClientsResponse>({
    query: GET_SMINEX_CLUB_CLIENTS_QUERY,
    variables: {
      page: withoutPagination ? null : currentPage,
      pageSize: withoutPagination ? null : CLIENTS_PAGE_SIZE,
      searchString: searchValue,
      orderBy,
      clientsFilter,
    },
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (fetching) return;

    if (data?.getSminexClubClients?.sminexClubClients) {
      const newClients: Client[] = data.getSminexClubClients.sminexClubClients;

      if (currentPage === 1) {
        setAllClients(newClients);
      } else {
        setAllClients((prev) => [...prev, ...newClients]);
      }

      const total = data.getSminexClubClients.total || 0;
      const loaded = allClients.length + newClients.length;
      setHasMore(loaded < total);
    }
  }, [data, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setAllClients([]);
  }, [searchValue, orderBy]);

  const loadMore = useCallback(() => {
    if (!fetching && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [fetching, hasMore]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleFilterChange = (value: ClientsFilter) => {
    setClientsFilter(value);
  };

  const handleOrderChange = (value: string | null) => {
    setOrderBy(value);
  };

  const refetch = useCallback(() => {
    setCurrentPage(1);
    setAllClients([]);
    executeQuery({ requestPolicy: 'network-only' });
  }, [executeQuery]);

  return {
    clients: allClients,
    fetching,
    error,
    currentPage,
    executeQuery,
    hasMore,
    loadMore,
    handleSearchChange,
    handleOrderChange,
    handleFilterChange,
    refetch,
    total: data?.getSminexClubClients?.total || 0,
  };
};
